import { useState } from 'react';

import { getRouteApi } from '@tanstack/react-router';
import { Modal } from 'cpool-components/general/Modal';
import { Tooltip, TooltipContent, TooltipTrigger } from 'cpool-components/general/Tooltip';
import { Button } from 'cpool-components/input/Button';
import { ButtonVariant } from 'cpool-components/input/Button/types';
import { AGGREGATE_NAMES, type EUROPACE_PROCESS_PLATFORM } from 'cpool-constants';
import type { SchemaLoanRequestResolver_Get } from 'cpool-tables/backend-resolvers';
import type { ValueOf } from 'cpool-utils';
import { useTranslation } from 'react-i18next';
import { LuCircleX, LuTrash2 } from 'react-icons/lu';

import { useAclPermissions } from '~/features/leadDetails/hooks/useAclPermissions';
import { useCensorCoApplicantMutation } from '~/features/leadDetails/hooks/useCensorCoApplicantMutation';

type Props = {
    lead: SchemaLoanRequestResolver_Get;
};

const PLATFORM_DISPLAY_NAMES: Record<EUROPACE_PROCESS_PLATFORM, string> = {
    EUROPACE2: 'Europace',
    STARPOOL: 'Starpool',
};

const ACTION_DISABLED_REASONS = {
    ALREADY_TRANSFERRED: 'alreadyTransferred',
    MISSING_PERMISSION: 'missingPermission',
} as const;

type ActionDisabledReason = ValueOf<typeof ACTION_DISABLED_REASONS>;

export const CensorCoApplicant: React.FC<Props> = ({ lead }) => {
    const { t } = useTranslation();
    const { leadId } = getRouteApi('/_app/leads_/details/$leadId').useParams();
    const { permissions } = useAclPermissions({ loanRequestId: leadId });
    const { mutate: censorCoApplicant } = useCensorCoApplicantMutation(leadId);
    const [showConfirm, setShowConfirm] = useState(false);
    const [openDisabledReasonTooltip, setOpenDisabledReasonTooltip] = useState(false);

    const hasPermission = permissions.commands[AGGREGATE_NAMES.LOAN_REQUEST].censorCoApplicant;
    const hasEuropaceProcess = lead?.europace?.id != null;
    const hasConsorsSubscription = lead?.consors?.status != null;
    const hasSecondApplicantAgreedToDataProtection = lead?.antragsteller2?.dataProtectionAgreed === true;

    // if applicant2 has not agreed to data protection, his data was not sent to europace/consors yet, so we can censor it
    const isCensoringBlocked =
        (hasEuropaceProcess || hasConsorsSubscription) && hasSecondApplicantAgreedToDataProtection;
    const applicantId = lead.antragsteller2?.id;

    const europacePlatform =
        lead?.europace?.platform != null ? PLATFORM_DISPLAY_NAMES[lead.europace.platform] : undefined;

    let confirmDisabledReason: ActionDisabledReason | undefined;
    if (!hasPermission) {
        confirmDisabledReason = ACTION_DISABLED_REASONS.MISSING_PERMISSION;
    } else if (isCensoringBlocked) {
        confirmDisabledReason = ACTION_DISABLED_REASONS.ALREADY_TRANSFERRED;
    }

    const isActionDisabled = confirmDisabledReason !== undefined;

    const onConfirm = () => {
        if (applicantId) censorCoApplicant({ applicantId });
        setShowConfirm(false);
    };

    return (
        <>
            <Modal
                open={showConfirm}
                onClose={() => setShowConfirm(false)}
                title={t('LEAD_DETAILS_GENERAL.CENSOR_CO_APPLICANT_CONFIRM.TITLE')}
                panelClassName="max-w-md rounded-lg"
            >
                <p className="text-base text-midnight">
                    {t('LEAD_DETAILS_GENERAL.CENSOR_CO_APPLICANT_CONFIRM.BODY', {
                        platform: europacePlatform,
                    })}
                </p>
                <div className="flex justify-end gap-2 pt-4">
                    <Button
                        type="button"
                        variant="secondary"
                        className="inline-flex items-center gap-2"
                        onClick={() => setShowConfirm(false)}
                    >
                        <LuCircleX size={16} aria-hidden />
                        {t('BUTTONS.CANCEL')}
                    </Button>
                    <Button
                        type="button"
                        className="inline-flex items-center gap-2 bg-blue-dark text-white hover:bg-opacity-80"
                        onClick={onConfirm}
                    >
                        <LuTrash2 size={16} aria-hidden />
                        {t('LEAD_DETAILS_GENERAL.CENSOR_CO_APPLICANT_CONFIRM.CONFIRM')}
                    </Button>
                </div>
            </Modal>
            <Tooltip
                open={openDisabledReasonTooltip}
                onOpenChange={(triggeredOpen) =>
                    setOpenDisabledReasonTooltip(triggeredOpen && confirmDisabledReason !== undefined)
                }
            >
                <TooltipTrigger>
                    <Button
                        onClick={(event) => {
                            event.preventDefault();
                            if (!isActionDisabled) setShowConfirm(true);
                        }}
                        disabled={isActionDisabled}
                        variant={ButtonVariant.SECONDARY}
                        iconLeft={<LuTrash2 size={14} />}
                    >
                        <span>{t('CREDIT_WORTHINESS.BUTTONS.CENSOR_CO_APPLICANT')}</span>
                    </Button>
                </TooltipTrigger>
                {confirmDisabledReason === ACTION_DISABLED_REASONS.MISSING_PERMISSION && (
                    <TooltipContent>
                        {t('LEAD_DETAILS_GENERAL.CENSOR_CO_APPLICANT_CONFIRM.TOOLTIP_DISABLED_NO_PERMISSION')}
                    </TooltipContent>
                )}
                {confirmDisabledReason === ACTION_DISABLED_REASONS.ALREADY_TRANSFERRED && (
                    <TooltipContent>
                        {t('LEAD_DETAILS_GENERAL.CENSOR_CO_APPLICANT_CONFIRM.TOOLTIP_DISABLED_ALREADY_TRANSFERRED')}
                    </TooltipContent>
                )}
            </Tooltip>
        </>
    );
};
